import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { PlaybackState } from '../playback-controller';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../toolbar-control.tokens';
import { VoyageTrackController } from '../voyage-track.controller';
import {
  PortCallRow,
  PortCallsState,
  VoyagePortCallsController,
} from '../voyage-port-calls.controller';
import { voyageSpendBlocker } from '../../voyage-radar/voyage-credits.model';
import { BaseControl } from './base-control';
import { relativeFixAge } from './toolbar-vessel-controls';

/**
 * Port calls — the anchor button and its drawer.
 *
 * The drawer machinery is identical to `app-track-entry-control`: same
 * `ToolbarDrawerService`, same cross-injected `VoyageTrackController` for
 * "which vessel is active", same `<app-toolbar-drawer>` shell.
 *
 * VISIBILITY IS DELIBERATELY DIFFERENT. Trace hides completely with nothing
 * selected, because a track needs a vessel to exist at all. Port calls does
 * not: the button is a STANDING AFFORDANCE — always on the bar — and the
 * drawer itself carries the "click a vessel" instruction plus the price. The
 * cost is therefore visible before a vessel is even picked, rather than the
 * bar hiding the entry point until the instruction has been satisfied.
 *
 * Reactivity is the same idea taken one step further: the drawer body is built
 * from two `async`-piped streams, so selecting a different vessel WHILE THE
 * DRAWER IS OPEN updates it in place. The user does not close the drawer, click
 * a marker and reopen it.
 *
 * Lives in its own file rather than in toolbar-vessel-controls.ts (which the
 * plan suggested): two components plus their styles would have tripled that
 * file, and one file per control family is the convention the neighbours
 * already follow. `relativeFixAge` is imported from there instead.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Aug" — a port call's date, in the only precision a berth needs. */
export function dayMonth(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/**
 * The drawer body. Presentational — every decision it renders was made in the
 * controller, so this can be read as the four states it has and nothing else.
 */
@Component({
  selector: 'app-port-calls-panel',
  template: `
    <!-- 1. No subject yet. The price rides on the drawer subtitle, so the cost
            of the action is known before a hull is even chosen. -->
    <div class="pc-note" *ngIf="!ports.subjectMmsi">
      Select a vessel on the map to see its port calls.
    </div>

    <ng-container *ngIf="ports.subjectMmsi">
      <!-- 2. Loading. -->
      <div class="pc-note pc-note--busy" *ngIf="ports.portCallsLoading">
        <mat-icon class="material-icons-outlined pc-spin">progress_activity</mat-icon>
        Fetching port calls…
      </div>

      <ng-container *ngIf="!ports.portCallsLoading">
        <!-- 3. Have a hull, never fetched. The button below is the way on. -->
        <div class="pc-note" *ngIf="!isFetched">
          No port-call history loaded for
          {{ ports.subjectName || 'MMSI ' + ports.subjectMmsi }} yet.
        </div>

        <ng-container *ngIf="isFetched">
          <!-- 4a. Fetched, and the operator knows nothing. Free, and said so. -->
          <div class="pc-note" *ngIf="!ports.rows.length">
            The operator has no port-call history for this vessel.
            You were not charged.
          </div>

          <!-- The count and the age of the data. Lives in the BODY, not in the
               drawer subtitle: in the header it competed with the vessel name
               and the ✕ inside a 360px card, and the name is the one thing that
               must never lose that fight. The spend has since left the header
               for the foot of this panel, but the reasoning is unchanged — a
               subtitle is not where a count belongs. -->
          <div class="pc-meta" *ngIf="ports.rows.length">
            showing {{ ports.rows.length
            }}<ng-container *ngIf="ports.total != null"> of {{ ports.total }}</ng-container
            ><ng-container *ngIf="fetchedAgo"> · fetched {{ fetchedAgo }}</ng-container>
          </div>

          <!-- 4b. Fetched, with rows. Newest first. -->
          <div class="pc-list" *ngIf="ports.rows.length">
            <div class="pc-row" *ngFor="let r of ports.rows; trackBy: trackRow"
                 [class.pc-row--in-port]="r.inPort">
              <span class="pc-row__mark">
                <mat-icon *ngIf="r.inPort" class="material-icons-outlined">anchor</mat-icon>
              </span>
              <span class="pc-row__text">
                <span class="pc-row__port">
                  {{ r.portName }}<span class="pc-row__country" *ngIf="r.portCountry">
                    · {{ r.portCountry }}</span>
                </span>
                <!-- The operator's own word, rendered as plain text rather than
                     mapped through a fixed set of labels: only "berthed" has
                     ever been observed, and an unseen value must degrade to
                     what was actually said instead of vanishing. -->
                <span class="pc-row__status" *ngIf="r.status">{{ r.status }}</span>
              </span>
              <span class="pc-row__when">
                <!-- Still there. -->
                <ng-container *ngIf="r.inPort">
                  in port<ng-container *ngIf="r.arrival"> · since {{ dm(r.arrival) }}</ng-container>
                </ng-container>
                <!-- Gone, and the operator timed it. -->
                <ng-container *ngIf="!r.inPort && r.departure">
                  {{ dm(r.arrival) }} &rarr; {{ dm(r.departure) }}<ng-container
                    *ngIf="r.stay"> · {{ r.stay }}</ng-container>
                </ng-container>
                <!-- Gone, but the operator never recorded WHEN. Common in real
                     data. Say what is known — the arrival — rather than
                     inventing an arrow to a date that does not exist. -->
                <ng-container *ngIf="!r.inPort && !r.departure">
                  <ng-container *ngIf="r.arrival">arrived {{ dm(r.arrival) }}</ng-container>
                  <ng-container *ngIf="!r.arrival">no dates recorded</ng-container>
                </ng-container>
              </span>
            </div>
          </div>

          <!-- A ceiling is never silent. Pagination is not built (plan §2.4),
               so saying so is the whole of the honesty budget here. -->
          <div class="pc-note pc-note--foot" *ngIf="ports.truncated">
            The operator holds more calls than one lookup returns.
          </div>
        </ng-container>

        <!-- The spend, in the same place the trace drawer puts its own: at the
             FOOT OF THE BODY, under the answer it acts on, with the cost line
             beneath it. It used to sit in the drawer header as a text action,
             which put the one priced control in this card in the chrome strip
             next to the ✕ — the lightest-weight position in the drawer for its
             heaviest act, and a different shape from the button doing the
             identical job one drawer over. -->
        <button type="button" class="tb-drawer__primary tb-drawer__primary--follows"
                [disabled]="!!blocker"
                [matTooltip]="tooltip"
                (click)="fetch()">
          <mat-icon class="material-icons-outlined">anchor</mat-icon>
          <span class="tb-drawer__primary-label">{{ actionLabel }}</span>
          <em>{{ costLabel }}</em>
        </button>

        <!-- Same two readings as the trace drawer's cost line, and the same
             reason for them: this button has no confirmation behind it, so the
             price and the balance have to be legible at the moment of pressing
             rather than one panel away. -->
        <div class="tb-drawer__cost">
          <ng-container *ngIf="blocker as why; else haveCredits">
            {{ why }}
          </ng-container>
          <ng-template #haveCredits>
            Uses {{ costLabel }}
            <ng-container *ngIf="ports.creditsRemaining != null">
              &middot; {{ ports.creditsRemaining }} remaining
            </ng-container>
          </ng-template>
        </div>
      </ng-container>
    </ng-container>`,
  styles: [`
    /* Reuses the .vh-row grammar from app-vessel-history-control so the two
       drawers read as one product. */
    .pc-list {
      display: flex;
      flex-direction: column;
      max-height: 260px;
      overflow-y: auto;
    }
    .pc-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 2px;
    }
    .pc-row + .pc-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .pc-row__mark {
      flex: 0 0 auto;
      width: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .pc-row__mark .mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      color: #B45309;
    }
    .pc-row__text {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      line-height: 1.25;
    }
    .pc-row__port {
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .pc-row__country { font-weight: 400; opacity: 0.75; }
    .pc-row__status {
      font-size: 10.5px;
      opacity: 0.65;
      text-transform: lowercase;
    }
    .pc-row__when {
      flex: 0 0 auto;
      font-size: 11px;
      opacity: 0.8;
      white-space: nowrap;
    }
    .pc-row--in-port .pc-row__when { font-weight: 600; opacity: 1; }
    .pc-note {
      font-size: 11.5px;
      opacity: 0.75;
      line-height: 1.4;
      padding: 8px 2px;
      max-width: 260px;
    }
    .pc-note--busy { display: flex; align-items: center; gap: 6px; }
    .pc-note--busy .mat-icon {
      font-size: 15px;
      width: 15px;
      height: 15px;
      line-height: 15px;
    }
    /* Its own spinner rather than the toolbar host's .tb-spin: this panel is
       rendered into a CDK overlay, outside the host's DOM subtree, so a class
       that reaches it depends on where that rule was scoped. Local is certain. */
    .pc-spin { animation: pc-spin 1.1s linear infinite; }
    @keyframes pc-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      .pc-spin { animation: none; }
    }
    .pc-note--foot {
      border-top: 1px solid rgba(128, 128, 128, 0.18);
      padding-top: 8px;
    }
    .pc-meta {
      font-size: 11px;
      opacity: 0.65;
      font-variant-numeric: tabular-nums;
      padding: 0 2px 6px;
    }
  `],
})
export class PortCallsPanelComponent {
  /**
   * Everything comes off the port-calls state — including WHICH hull. It used
   * to take the track state too and read `activeMmsi` from it, which is null
   * for every vessel popup except the playback layer's.
   */
  @Input() ports!: PortCallsState;

  /**
   * The panel owns the spend, exactly as `app-track-options-panel` owns the
   * Trace button — the button lives in the body, so the handler belongs with it
   * rather than being relayed back out through the control's header bindings.
   */
  constructor(private controller: VoyagePortCallsController) {}

  /**
   * "Fetched" means fetched FOR THIS HULL. An empty answer still counts — it is
   * an answer, and it was free — which is what stops the drawer dropping back
   * to "not loaded yet" and re-offering a spend that already happened.
   */
  get isFetched(): boolean {
    return !!this.ports.subjectMmsi && this.ports.fetchedForMmsi === this.ports.subjectMmsi;
  }

  get costLabel(): string {
    const n = this.ports.portCallCredits;
    return `${n} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /** "Refresh" once an answer is held, because the first one is still on screen. */
  get actionLabel(): string {
    return this.isFetched ? 'Refresh' : 'Get port calls';
  }

  get tooltip(): string {
    return this.isFetched
      ? 'Ask the operator for this history again'
      : 'Fetch this vessel’s port-call history from the operator';
  }

  /**
   * What this button must go DEAD for, or null when it stays live — via
   * `voyageSpendBlocker` rather than an inline comparison, so every priced
   * Voyage Radar control draws the same line in the same place. An empty wallet
   * blocks; an ended TOI does not, staying pressable so `fetchPortCalls` can
   * answer the press with the modal. An UNKNOWN balance (null) means it was
   * never reported, not that it is spent, and `null < cost` is TRUE in JS — a
   * naive test would refuse a user the server would allow. The server's 402 is
   * what refuses on unknown, if anything does.
   */
  get blocker(): string | null {
    return voyageSpendBlocker({
      action: 'portCalls',
      cost: this.ports.portCallCredits,
      remaining: this.ports.creditsRemaining,
      toiEnded: this.ports.toiEnded,
    });
  }

  /**
   * Spend. Unlike the trace panel this does NOT close the drawer first: a track
   * is drawn on the map behind the card, so dismissing it reveals the result,
   * whereas these rows land in this very panel and closing it would hide the
   * thing just bought. The in-flight guard therefore has to be the controller's
   * own `portCallsLoading`, which also swaps this button for the busy note.
   */
  fetch(): void {
    this.controller.fetch(this.ports.subjectMmsi, this.ports.subjectName);
  }

  /** "6 d ago" — how old the held answer is, or null when it cannot be said. */
  get fetchedAgo(): string | null {
    return this.ports.fetchedAt ? relativeFixAge(this.ports.fetchedAt) : null;
  }

  dm(iso: string | null): string {
    return dayMonth(iso);
  }

  trackRow(i: number, r: PortCallRow): string {
    return `${i}:${r.portName}:${r.arrival ?? ''}`;
  }
}

@Component({
  selector: 'app-port-call-control',
  template: `
    <!-- The wrapper is how the tooltip reaches the price, which lives in
         controller state (a toolbar control has no sensor of its own). It is
         NOT a visibility condition: a BehaviorSubject emits synchronously, so
         this always renders and the button stays the standing affordance. -->
    <ng-container *ngIf="ports.state$ | async as ps">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Port calls"
              [matTooltip]="'Port calls · ' + costLabel(ps)"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">anchor</mat-icon>
      </button>
    </ng-container>

    <!-- Declared outside the wrapper, like every drawer panel here: this button
         never disappears today, but a future edit that DOES gate it must not
         silently orphan an open overlay's TemplateRef. -->
    <ng-template #panel>
      <ng-container *ngIf="ports.state$ | async as ps">
        <ng-container *ngIf="track.state$ | async as ts">
          <!-- No actionLabel binding: the spend is a primary button at the foot
               of the panel body, where the trace drawer puts its own. The
               header is title, subtitle and ✕ — chrome only. -->
          <app-toolbar-drawer
              [title]="drawerTitle(ps)"
              [subtitle]="drawerSubtitle(ps)"
              [accent]="accentFor(ts, ps)"
              (close)="drawer.close()">
            <app-port-calls-panel [ports]="ps"></app-port-calls-panel>
          </app-toolbar-drawer>
        </ng-container>
      </ng-container>
    </ng-template>`,
})
export class PortCallControlComponent extends BaseControl implements AfterViewInit, OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /** "Which vessel is active" — the same stream Trace reads. */
    public track: VoyageTrackController,
    /** Fetched rows, loading, stored-ness, and the price. */
    public ports: VoyagePortCallsController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /**
   * The popup's anchor icon OPENS this drawer rather than spending on its own,
   * so a card opened by accident can never buy anything. `ngAfterViewInit`, not
   * the constructor — `triggerRef` does not exist until the view is built.
   */
  private _openFn = () => this.drawer.open(this.panel, this.vcr, this.triggerRef);

  ngAfterViewInit(): void {
    this.ports.onRequestOpen = this._openFn;
  }

  costLabel(ps: PortCallsState): string {
    return `${ps.portCallCredits} ${ps.portCallCredits === 1 ? 'credit' : 'credits'}`;
  }

  /**
   * The selection colour, but only while the drawer's subject IS the selected
   * vessel. A popup-opened subject that was never selected has no colour on the
   * map, and borrowing another hull's would point at the wrong arrow.
   */
  accentFor(ts: PlaybackState, ps: PortCallsState): string | null {
    return ts.activeMmsi && ts.activeMmsi === ps.subjectMmsi
      ? ts.activeVesselColor ?? null
      : null;
  }

  /**
   * Whose history this is. Names the hull even when the operator gave us no
   * ship name — a bare 9-digit number in a bold title reads as a serial, so it
   * is labelled.
   */
  drawerTitle(ps: PortCallsState): string {
    if (ps.subjectName) return ps.subjectName;
    return ps.subjectMmsi ? `MMSI ${ps.subjectMmsi}` : 'Port calls';
  }

  /**
   * Kept SHORT on purpose. The header holds the title, this and the ✕ inside a
   * 360px card; the count and the age of the data live in the panel body, where
   * they have room. What is left is the MMSI, and only when the title is showing
   * a name instead of it.
   *
   * The exception is the no-subject state, which is the one time this carries
   * the price: with no hull there is no spend button in the body to carry it,
   * and the cost of the action should be legible before a vessel is picked.
   */
  drawerSubtitle(ps: PortCallsState): string | null {
    if (!ps.subjectMmsi) return `${this.costLabel(ps)} per lookup`;
    return ps.subjectName ? ps.subjectMmsi : null;
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
    // Only clear it if THIS instance owns it. A slot swap (live → track) tears
    // one instance down and stands another up, and the new one's own
    // ngAfterViewInit must win rather than lose to the old one's cleanup.
    if (this.ports.onRequestOpen === this._openFn) this.ports.onRequestOpen = null;
  }
}
